import { useCallback, useEffect, useRef, useState } from 'react';
import { isImageAttachment } from '../common/courseFiles';
import { putWithProgress } from '../common/r2Put';

let uploadSeq = 0;
const nextId = () => {
    uploadSeq += 1;
    return `upload-${Date.now()}-${uploadSeq}`;
};

/**
 * Cola de subidas a R2 con estado por archivo (uploading / ready / error).
 */
export const useR2UploadQueue = ({ validateFile, requestPresign, maxFiles = 10 }) => {
    const [items, setItems] = useState([]);
    const xhrMap = useRef(new Map());
    const previewMap = useRef(new Map());

    const releasePreview = useCallback((id) => {
        const url = previewMap.current.get(id);
        if (url) {
            URL.revokeObjectURL(url);
            previewMap.current.delete(id);
        }
    }, []);

    const abortItem = useCallback((id) => {
        const xhr = xhrMap.current.get(id);
        xhr?.abort();
        xhrMap.current.delete(id);
        releasePreview(id);
    }, [releasePreview]);

    const clearAll = useCallback(() => {
        [...xhrMap.current.keys()].forEach(abortItem);
        setItems([]);
    }, [abortItem]);

    useEffect(() => () => {
        [...xhrMap.current.keys()].forEach((id) => {
            xhrMap.current.get(id)?.abort();
            const url = previewMap.current.get(id);
            if (url) URL.revokeObjectURL(url);
        });
        xhrMap.current.clear();
        previewMap.current.clear();
    }, []);

    const removeItem = useCallback((id) => {
        abortItem(id);
        setItems((prev) => prev.filter((item) => item.id !== id));
    }, [abortItem]);

    const startUploads = useCallback(async (files, { remainingSlots } = {}) => {
        const incoming = Array.from(files || []).filter(Boolean);
        if (!incoming.length) return { ok: true, failures: [] };

        const activeCount = items.filter((item) => item.status !== 'error').length;
        const slots = Number.isFinite(remainingSlots)
            ? remainingSlots
            : Math.max(0, maxFiles - activeCount);

        if (incoming.length > slots) {
            return {
                ok: false,
                blocked: true,
                message: slots <= 0
                    ? `Ya alcanzaste el máximo de ${maxFiles} archivos.`
                    : `Solo puedes agregar ${slots} archivo${slots === 1 ? '' : 's'} más.`,
                remaining: slots,
            };
        }

        const staged = incoming.map((file) => {
            const validation = validateFile(file);
            const id = nextId();
            if (!validation.valid) {
                return {
                    id,
                    file: null,
                    fileName: file.name,
                    mimeType: file.type,
                    size: file.size,
                    previewUrl: null,
                    status: 'error',
                    progress: 0,
                    payload: null,
                    error: validation.message,
                };
            }

            const previewUrl = isImageAttachment(validation.mimeType)
                ? URL.createObjectURL(file)
                : null;
            if (previewUrl) previewMap.current.set(id, previewUrl);

            return {
                id,
                file,
                fileName: file.name,
                mimeType: validation.mimeType,
                size: file.size,
                previewUrl,
                status: 'uploading',
                progress: 0,
                payload: null,
                error: null,
            };
        });

        setItems((prev) => [...prev, ...staged]);

        const uploads = staged.filter((item) => item.status === 'uploading');
        await Promise.all(uploads.map(async (item) => {
            try {
                const presign = await requestPresign(item.file, item.mimeType);
                if (!presign?.enabled || !presign?.uploadUrl) {
                    throw new Error(presign?.message || 'La subida de archivos no está disponible.');
                }

                await putWithProgress({
                    uploadUrl: presign.uploadUrl,
                    file: item.file,
                    mimeType: item.mimeType,
                    onProgress: (progress) => {
                        setItems((prev) => prev.map((entry) => (
                            entry.id === item.id ? { ...entry, progress } : entry
                        )));
                    },
                    onXhr: (xhr) => { xhrMap.current.set(item.id, xhr); },
                });

                xhrMap.current.delete(item.id);
                setItems((prev) => prev.map((entry) => (
                    entry.id === item.id
                        ? {
                            ...entry,
                            status: 'ready',
                            progress: 100,
                            file: null,
                            payload: {
                                fileName: item.fileName,
                                mimeType: item.mimeType,
                                size: item.size,
                                provider: presign.provider || 'r2',
                                storageKey: presign.storageKey,
                                url: presign.publicUrl,
                            },
                        }
                        : entry
                )));
            } catch (error) {
                xhrMap.current.delete(item.id);
                if (error?.aborted) {
                    setItems((prev) => prev.filter((entry) => entry.id !== item.id));
                    return;
                }
                releasePreview(item.id);
                setItems((prev) => prev.map((entry) => (
                    entry.id === item.id
                        ? {
                            ...entry,
                            status: 'error',
                            file: null,
                            error: error?.response?.data?.message || error?.message || 'No se pudo adjuntar el archivo.',
                        }
                        : entry
                )));
            }
        }));

        return { ok: true, failures: [] };
    }, [items, maxFiles, validateFile, requestPresign, releasePreview]);

    const readyPayloads = items
        .filter((item) => item.status === 'ready' && item.payload)
        .map((item) => item.payload);

    return {
        items,
        readyPayloads,
        isUploading: items.some((item) => item.status === 'uploading'),
        hasErrors: items.some((item) => item.status === 'error'),
        startUploads,
        removeItem,
        clearAll,
    };
};
